import { useReducer } from 'react';

import { useLocale } from './useLocale';
import {
  TDocuments,
  TFileStatusGroup,
} from '../api/lendingApi/lendingApiQueries';
import { TUploadRejectReasonCode } from '../components/TradingHistory/UploadButton/UploadButton';
import { TFileStatus } from '../components/TradingHistory/UploadedFiles/UploadedFiles';
import { translateUploadRejectReason } from '../helpers/rejectReasonHelper';

enum ACTIONS {
  SET_ALL_FILES = 'SET_ALL_FILES',
  SET_FILE_LOADING = 'SET_FILE_LOADING',
  SET_FILE_SUCCESS = 'SET_FILE_SUCCESS',
  SET_FILE_ERROR = 'SET_FILE_ERROR',
  SET_FILE_REJECT = 'SET_FILE_REJECT',
  REMOVE_FILE = 'REMOVE_FILE',
}

export type TFile = {
  id: string;
  fileName: string;
  status: TFileStatus;
  statusGroup: TFileStatusGroup;
  errorReason?: string;
  rejectReason?: string;
};

type TSetAllFilesAction = {
  type: typeof ACTIONS.SET_ALL_FILES;
  payload: {
    files: {
      id: string;
      attributes: {
        document_name: string;
        status: string;
        reject_reason?: TUploadRejectReasonCode;
      };
    }[];
  };
};

type TSetRemoveAction = {
  type: ACTIONS.REMOVE_FILE;
  payload: {
    id: string;
  };
};

type TSetLoadingAction = {
  type: ACTIONS.SET_FILE_LOADING;
  payload: {
    id: string;
    fileName: string;
  };
};

type TSetSuccessAction = {
  type: ACTIONS.SET_FILE_SUCCESS;
  payload: {
    id: string;
    status: TFileStatus;
  };
};

type TSetErrorAction = {
  type: ACTIONS.SET_FILE_ERROR;
  payload: {
    id: string;
    errorReason: string;
  };
};

type TSetRejectAction = {
  type: ACTIONS.SET_FILE_REJECT;
  payload: {
    id: string;
    rejectReason: TUploadRejectReasonCode;
  };
};

type TReducerAction =
  | TSetAllFilesAction
  | TSetRemoveAction
  | TSetLoadingAction
  | TSetSuccessAction
  | TSetErrorAction
  | TSetRejectAction;

export function useFileUploadReducer() {
  const { isDE } = useLocale();

  const reducer = (state: TFile[], action: TReducerAction) => {
    switch (action.type) {
      case ACTIONS.SET_ALL_FILES:
        return [
          ...state,
          ...(action.payload.files ?? []).map((file) => {
            const isRejected = file.attributes.status === 'rejected';

            const fileData: TFile = {
              id: file.id,
              fileName: file.attributes.document_name,
              status: isRejected
                ? ('rejected' as TFileStatus)
                : ('success' as TFileStatus),
              statusGroup: isRejected
                ? ('rejected' as TFileStatusGroup)
                : file.attributes.status === 'complete'
                  ? ('approved' as TFileStatusGroup)
                  : ('uploaded' as TFileStatusGroup),
            };

            if (isRejected && !file.attributes.reject_reason) {
              fileData.rejectReason =
                'Document cannot be validated. Please upload another document.';
            }

            if (isRejected && file.attributes.reject_reason) {
              fileData.rejectReason = translateUploadRejectReason(
                file.attributes.reject_reason,
              );
            }
            return fileData;
          }),
        ];
      case ACTIONS.SET_FILE_LOADING:
        return [
          ...state,
          {
            id: action.payload.id,
            fileName: action.payload.fileName,
            status: 'loading' as TFileStatus,
            statusGroup: 'uploaded' as TFileStatusGroup,
          },
        ];
      case ACTIONS.SET_FILE_ERROR: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );
        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: 'error' as TFileStatus,
          errorReason: action.payload.errorReason,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.SET_FILE_REJECT: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );

        const rejectReason = action.payload.rejectReason
          ? translateUploadRejectReason(action.payload.rejectReason)
          : 'Document cannot be validated. Please upload another document.';

        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: 'rejected' as TFileStatus,
          rejectReason: rejectReason,
          statusGroup: 'rejected' as TFileStatusGroup,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.SET_FILE_SUCCESS: {
        const fileToUpdateIndex = state.findIndex(
          (file) => file.id === action.payload.id,
        );
        const updatedFile = {
          ...state[fileToUpdateIndex],
          status: action.payload.status,
        };

        return state
          .slice(0, fileToUpdateIndex)
          .concat(updatedFile)
          .concat(state.slice(fileToUpdateIndex + 1));
      }
      case ACTIONS.REMOVE_FILE:
        return state.filter((file) => file.id !== action.payload.id);
      default:
        return state;
    }
  };

  const [uploadedFiles, dispatch] = useReducer(reducer, []);

  const setAllFiles = (files: TDocuments[]) => {
    dispatch({
      type: ACTIONS.SET_ALL_FILES,
      payload: {
        files,
      },
    });
  };

  const setFileLoading = (id: string, fileName: string) => {
    dispatch({
      type: ACTIONS.SET_FILE_LOADING,
      payload: {
        id,
        fileName,
      },
    });
  };

  const setFileSuccess = (id: string) => {
    dispatch({
      type: ACTIONS.SET_FILE_SUCCESS,
      payload: {
        id,
        status: 'success',
      },
    });
  };

  const setFileError = (
    id: string,
    error: { code: string; detail: string },
  ) => {
    let defaultErrorReason = error.detail;

    if (isDE) {
      switch (error.code) {
        case 'DocumentUploadError':
          defaultErrorReason =
            'Das Hochladen des Dokuments ist fehlgeschlagen. Bitte versuchen Sie es später noch einmal.';
          break;
        case 'DuplicateDocumentError':
          defaultErrorReason = 'Das eingereichte Dokument existiert bereits.';
          break;
        case 'ValidationError':
          defaultErrorReason =
            'Das eingereichte Dokument ist ungültig (die Dateiendung ist nicht erlaubt oder das Dokument ist leer).';
          break;
        default:
          defaultErrorReason = 'Das eingereichte Dokument ist ungültig.';
      }
    }
    dispatch({
      type: ACTIONS.SET_FILE_ERROR,
      payload: {
        id,
        errorReason: defaultErrorReason,
      },
    });
  };

  const setFileReject = (id: string, rejectReason: TUploadRejectReasonCode) => {
    dispatch({
      type: ACTIONS.SET_FILE_REJECT,
      payload: {
        id,
        rejectReason,
      },
    });
  };

  const removeFile = (id: string) => {
    dispatch({
      type: ACTIONS.REMOVE_FILE,
      payload: {
        id,
      },
    });
  };

  return {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  };
}
